import { useState } from 'react';
import {
  EDUCATION_DELIVERY_MODE_LABEL,
  type EducationMemberOption,
} from '@platform/billing';
import { LuChevronDown } from '@platform/icon';
import type { EducationCartItem } from '../../model/education-cart.type';
import type { SelectedAttendee } from '../../model/education-checkout.type';
import {
  formatEducationPrice,
  formatSessionDateTime,
} from '../../lib/education-format';
import { EducationMemberPicker } from '../EducationMemberPicker';

interface CheckoutBusinessAttendeeSectionProps {
  items: EducationCartItem[];
  attendeesBySession: Record<string, SelectedAttendee[]>;
  members: EducationMemberOption[];
  membersLoading: boolean;
  membersError: boolean;
  disabled: boolean;
  onAddMember: (sessionId: string, member: EducationMemberOption) => void;
  onRemoveMember: (sessionId: string, userId: string) => void;
  /** 첫 회차 명단을 나머지 회차에 복사한다. */
  onApplyFirstToAll: () => void;
}

/** 기업 접수의 회차별 수강자 — 회차마다 활성 구성원을 골라 명단을 만든다. */
export function CheckoutBusinessAttendeeSection({
  items,
  attendeesBySession,
  members,
  membersLoading,
  membersError,
  disabled,
  onAddMember,
  onRemoveMember,
  onApplyFirstToAll,
}: CheckoutBusinessAttendeeSectionProps) {
  /**
   * 접어둔 회차. **여는 쪽이 아니라 접는 쪽을 담는다** — 목록에 없는 회차(장바구니에
   * 나중에 추가된 것)가 기본으로 열려 있어야 하기 때문이다.
   *
   * 첫 회차만 펴 두는 이유: 회차를 여러 개 담으면 명단+선택 목록이 회차마다 쌓여
   * 화면이 길어진다. 접힌 회차도 머리글에 인원이 그대로 보이므로 정보는 잃지 않는다.
   */
  const [collapsedSessions, setCollapsedSessions] = useState<Set<string>>(
    () => new Set(items.slice(1).map((item) => item.sessionId)),
  );
  const isSessionOpen = (sessionId: string) =>
    !collapsedSessions.has(sessionId);
  const toggleSession = (sessionId: string) =>
    setCollapsedSessions((previous) => {
      const next = new Set(previous);
      if (!next.delete(sessionId)) next.add(sessionId);
      return next;
    });
  const allSessionsOpen = collapsedSessions.size === 0;
  const toggleAllSessions = () =>
    setCollapsedSessions(
      allSessionsOpen
        ? new Set(items.map((item) => item.sessionId))
        : new Set(),
    );

  const firstSessionId = items[0]?.sessionId;
  const canApplyFirstToAll =
    (attendeesBySession[firstSessionId ?? '']?.length ?? 0) > 0;

  return (
    <section
      className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-transparent"
      aria-labelledby="attendee-heading"
    >
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-200 bg-gray-50/70 px-5 py-4 dark:border-white/10 dark:bg-white/[0.03]">
        <div>
          <h2
            id="attendee-heading"
            className="text-base font-semibold text-gray-950 dark:text-neutral-100"
          >
            회차별 수강자
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">
            구성원을 선택하면 연락처와 이메일이 자동으로 입력됩니다.
          </p>
        </div>
        {items.length > 1 && (
          <div className="flex items-center gap-3">
            {canApplyFirstToAll && (
              <button
                type="button"
                disabled={disabled}
                onClick={onApplyFirstToAll}
                className="whitespace-nowrap rounded-sm text-xs font-semibold text-brand-700 underline underline-offset-4 hover:text-brand-800 disabled:cursor-not-allowed disabled:text-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-brand-300 dark:hover:text-brand-200"
              >
                첫 회차 인원을 전체 적용
              </button>
            )}
            <button
              type="button"
              onClick={toggleAllSessions}
              className="whitespace-nowrap rounded-sm text-xs font-semibold text-gray-500 underline underline-offset-4 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-neutral-400 dark:hover:text-white"
            >
              {allSessionsOpen ? '전체 접기' : '전체 펼치기'}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3 p-5">
        {membersLoading && (
          <p className="py-8 text-center text-sm text-gray-500">
            기업 구성원을 불러오는 중…
          </p>
        )}
        {membersError && (
          <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            기업 구성원을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
          </p>
        )}

        {items.map((item, index) => {
          const attendees = attendeesBySession[item.sessionId] ?? [];
          const open = isSessionOpen(item.sessionId);
          const empty = attendees.length === 0;
          const panelId = `attendees-${item.sessionId}`;
          return (
            /*
             * 회차 하나가 카드 하나다. 구분선만으로 나누면 접었을 때 회차들이 한 덩어리로
             * 읽혀, 지금 어느 회차의 명단을 고치는지 흐려진다. 펼친 카드는 테두리·바탕을
             * 바꿔 지금 열려 있는 곳을 한눈에 잡아 준다.
             */
            <section
              key={item.sessionId}
              className={`overflow-hidden rounded-lg border transition-colors ${
                open
                  ? 'border-brand-300 bg-brand-50/40 dark:border-brand-500/40 dark:bg-brand-500/[0.06]'
                  : empty
                    ? 'border-red-200 bg-white hover:border-red-300 dark:border-red-900/50 dark:bg-transparent'
                    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/10 dark:bg-transparent dark:hover:border-white/20'
              }`}
            >
              {/* 머리글 한 줄 전체가 여닫기 버튼이다 — 화살표만 조준하게 만들지 않는다. */}
              <button
                type="button"
                onClick={() => toggleSession(item.sessionId)}
                aria-expanded={open}
                aria-controls={panelId}
                className="group flex w-full items-start gap-3 px-4 py-3.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-brand-500"
              >
                {/* 순번 — 회차명이 비슷할 때 카드끼리 구별되는 가장 빠른 단서다. */}
                <span
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold tabular-nums ${
                    open
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-neutral-300'
                  }`}
                >
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block font-semibold ${
                      open
                        ? 'text-brand-800 dark:text-brand-200'
                        : 'text-gray-950 group-hover:text-brand-700 dark:text-neutral-100 dark:group-hover:text-brand-300'
                    }`}
                  >
                    {item.sessionTitle}
                  </span>
                  <span className="mt-1 block text-xs text-gray-500 dark:text-neutral-400">
                    {item.courseTitle} ·{' '}
                    {EDUCATION_DELIVERY_MODE_LABEL[item.deliveryMode]} ·{' '}
                    {formatSessionDateTime(item.startAt)}
                  </span>
                </span>
                {/* 접힌 상태에서도 인원은 보여야 한다 — 수강자 없는 회차가 숨은 채로 결제가 막히면 원인을 못 찾는다. */}
                <span
                  className={`whitespace-nowrap text-sm font-medium tabular-nums ${
                    empty
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-700 dark:text-neutral-300'
                  }`}
                >
                  {empty
                    ? '수강자 없음'
                    : `${attendees.length}명 · ${formatEducationPrice(item.price * attendees.length)}`}
                </span>
                <LuChevronDown
                  aria-hidden
                  className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:text-gray-600 dark:group-hover:text-neutral-200 ${
                    open ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <div
                id={panelId}
                hidden={!open}
                className="border-t border-brand-200/70 bg-white px-4 pb-4 dark:border-white/10 dark:bg-transparent"
              >
                {!membersError && !membersLoading && (
                  <EducationMemberPicker
                    members={members}
                    sessionId={item.sessionId}
                    selectedUserIds={attendees.flatMap((attendee) =>
                      attendee.sourceUserId ? [attendee.sourceUserId] : [],
                    )}
                    disabled={disabled}
                    onToggle={(member, checked) =>
                      checked
                        ? onAddMember(item.sessionId, member)
                        : onRemoveMember(item.sessionId, member.userId)
                    }
                  />
                )}
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
}
